import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Package, Trash2, FileText } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { DealPdfShareDialog } from "../../components/DealPdfShareDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { PageHeader, Card, StatusBadge, EmptyState, Loading, Pager } from "../../components/common";
import { api, errMsg, mediaUrl } from "../../lib/api";
import { pkr, commissionText, DEAL_STATUS, AVAILABILITY } from "../../lib/format";

export default function ManageDeals() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const load = () => api.get("/deals", { params: { q, status, page, page_size: 20 } }).then((r) => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, page]); // eslint-disable-line
  const setDealStatus = async (d, s) => {
    try { await api.post(`/admin/deals/${d.id}/status`, { status: s }); toast.success(`${d.name} is now ${s}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [del, setDel] = useState(null);
  const doDelete = async () => {
    try { await api.delete(`/admin/deals/${del.id}`); toast.success(`${del.name} deleted`); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div>
      <PageHeader title="Manage Deals" subtitle="Create listings, set fixed prices and commissions, publish or archive."
        actions={<Button asChild data-testid="new-deal-button"><Link to="/admin/deals/new"><Plus className="mr-2 h-4 w-4" />New deal</Link></Button>} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <Input data-testid="deals-admin-search" placeholder="Search name, reference, category" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-11 sm:max-w-xs" />
        <div className="flex gap-2 overflow-x-auto">{["", "draft", "published", "archived"].map((s) => (
          <button key={s || "all"} onClick={() => { setStatus(s); setPage(1); }} data-testid={`deals-admin-filter-${s || "all"}`}
            className={`rounded-full border px-4 py-2 text-sm font-medium capitalize ${status === s ? "border-[#0B192C] bg-[#0B192C] text-white" : "bg-white"}`}>{s || "All"}</button>
        ))}</div>
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <EmptyState icon={Package} title="No deals yet" message="Create your first deal with a fixed price and commission." /> : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Deal</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Commission</TableHead><TableHead>Availability</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((d) => (
                <TableRow key={d.id} data-testid={`admin-deal-row-${d.ref}`}>
                  <TableCell className="min-w-[240px]"><div className="flex items-center gap-3">
                    <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-slate-100">{d.main_image && <img src={mediaUrl(d.main_image)} alt="" className="h-full w-full object-cover" />}</div>
                    <div><p className="font-semibold">{d.name}</p><p className="font-mono text-xs text-slate-400">{d.ref} · {d.category}</p></div></div></TableCell>
                  <TableCell className="text-right font-mono">{pkr(d.price)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{commissionText(d)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{AVAILABILITY[d.availability]}{d.stock_qty != null && ` (${d.stock_qty})`}</TableCell>
                  <TableCell><StatusBadge map={DEAL_STATUS} value={d.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right space-x-2">
                    <Button asChild size="sm" variant="outline"><Link to={`/admin/deals/${d.id}`} data-testid={`edit-deal-${d.ref}`}>Edit</Link></Button>
                    {d.status !== "published" && <Button size="sm" onClick={() => setDealStatus(d, "published")} data-testid={`publish-deal-${d.ref}`}>Publish</Button>}
                    {d.status === "published" && <Button size="sm" variant="outline" onClick={() => setDealStatus(d, "draft")} data-testid={`unpublish-deal-${d.ref}`}>Unpublish</Button>}
                    {d.status !== "archived" && <Button size="sm" variant="ghost" onClick={() => setDealStatus(d, "archived")} data-testid={`archive-deal-${d.ref}`}>Archive</Button>}
                    {d.status === "published" && <DealPdfShareDialog d={d} trigger={<Button size="sm" variant="outline" data-testid={`pdf-deal-${d.ref}`}><FileText className="mr-1 h-4 w-4" />Share</Button>} />}
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDel(d)} data-testid={`delete-deal-${d.ref}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-4"><Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} /></div>
        </Card>
      )}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{del?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the deal. Deals that already have orders cannot be deleted — archive them instead so order and commission records stay intact.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-deal-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-deal-confirm">Delete deal</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
